<template>
  <div class="filters-sidebar search-new-filters-panel">
    <!-- Header — title only; reset is exclusively the footer's fsb-reset-btn, so no top-level clear link. -->
    <div class="fsb-header">
      <span class="fsb-title">{{ $t('search.filtersTitle') }}</span>
    </div>

    <!-- SmartFilter (free text) — outside the accordion, using AutocompleteProduct, so
         typing hits GET /api/searchfilters/autocomplete and shows the grouped issuers/products/sids
         suggestions dropdown, not just a plain textbox. Searches immediately on change/selection —
         the only filter that does.
         skip-input-when-actionable: picking a product/sid navigates to its detail page
         (openProductDetails), but must NOT also apply the picked
         name as a filter first — that stray filter/badge/filtered-result-list would only become
         visible if the navigation doesn't happen (e.g. a routing mismatch) or the user returns to
         this page. -->
    <div v-if="smartFilter" class="p-2 fsb-search-wrap-new">
      <autocomplete-product
        :value="smartFilterValue"
        skip-input-when-actionable
        @input="onSmartFilterChange"
      ></autocomplete-product>
    </div>

    <div class="fsb-body search-new-fsb-body">
      <filter-accordion-section
        v-for="(group, index) in groups"
        :key="group.groupKey"
        :title="groupLabel(group.groupKey)"
        :start-open="false"
        :last="index === groups.length - 1 && otherUngrouped.length === 0"
      >
        <!-- .fsb-field's gap (global main.scss) is meant to space out SIBLING filter rows, not the
             label from its own widget — hence label+select-wrap are nested in their own inner div.
             Without that inner div, .fsb-field's gap would apply directly between the label and the
             widget, adding an extra gap. -->
        <div v-for="filter in group.filters" :key="filter.filterKey" class="fsb-field">
          <div>
            <label class="fsb-label">{{ filterLabel(filter.filterKey) }}</label>
            <div class="fsb-select-wrap">
              <filter-field
                :filter="filter"
                :value="pendingFilterValue(filter.filterKey)"
                @input="(value) => onFilterInput(filter.filterKey, value)"
              ></filter-field>
            </div>
          </div>
        </div>
      </filter-accordion-section>

      <filter-accordion-section v-if="otherUngrouped.length > 0" :title="$t('search.ungrouped')" :last="true">
        <div v-for="filter in otherUngrouped" :key="filter.filterKey" class="fsb-field">
          <div>
            <label class="fsb-label">{{ filterLabel(filter.filterKey) }}</label>
            <div class="fsb-select-wrap">
              <filter-field
                :filter="filter"
                :value="pendingFilterValue(filter.filterKey)"
                @input="(value) => onFilterInput(filter.filterKey, value)"
              ></filter-field>
            </div>
          </div>
        </div>
      </filter-accordion-section>
    </div>

    <!-- Footer -->
    <div class="fsb-footer">
      <button class="fsb-reset-btn" :disabled="!hasPendingFilters" @click="clearFilters">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 .49-3.5" />
        </svg>
        {{ $t('common.reset') }}
      </button>
      <button class="fsb-apply-btn" @click="applyPendingFilters">{{ $t('search.label.applyFilters') }}</button>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import FilterField from './FilterField'
import FilterAccordionSection from './FilterAccordionSection'
import AutocompleteProduct from './SearchFilters/AutocompleteProduct'

const { mapState, mapGetters, mapActions } = createNamespacedHelpers('search')

// Renders the whole filters-catalog response: groups ordered by group_order (already
// sorted by the BE), filters ordered by filter_order within each group, plus the ungrouped tail
// (SmartFilter excluded from that tail — it gets its own top search box).
// Filter widgets edit a DRAFT (pendingFilters); only "Applica filtri" runs the search
// (Apply/Reset flow). SmartFilter is the one exception — it searches immediately.
export default {
  name: 'filtersPanel',
  components: { FilterField, FilterAccordionSection, AutocompleteProduct },
  computed: {
    ...mapState({
      catalog: (state) => state.catalog,
    }),
    ...mapGetters(['pendingFilterValue', 'hasPendingFilters']),
    groups() {
      return this.catalog.groups || []
    },
    smartFilter() {
      return (this.catalog.ungrouped || []).find((f) => f.filterKey === 'SmartFilter') || null
    },
    otherUngrouped() {
      return (this.catalog.ungrouped || []).filter((f) => f.filterKey !== 'SmartFilter')
    },
    smartFilterValue() {
      return this.pendingFilterValue('SmartFilter') || ''
    },
  },
  methods: {
    ...mapActions(['setPendingFilterValue', 'applyFilterValueImmediately', 'applyPendingFilters', 'clearFilters']),
    onFilterInput(key, value) {
      this.setPendingFilterValue({ key, value })
    },
    // AutocompleteProduct's underlying CustomAutocompGroupedSelect binds a document-wide "click
    // outside" listener (v-closable) that re-emits 'input' with the CURRENT text on every click
    // anywhere on the page — not just on an actual edit. Without this guard, every click on the
    // page (Perimeter dropdowns included) would dispatch an unconditional search.
    onSmartFilterChange(value) {
      if ((value || '') === this.smartFilterValue) {
        return
      }
      this.applyFilterValueImmediately({ key: 'SmartFilter', value: value || null })
    },
    groupLabel(groupKey) {
      const key = 'search.groups.' + groupKey
      return this.$te(key) ? this.$t(key) : groupKey
    },
    filterLabel(filterKey) {
      const key = 'search.filters.' + filterKey
      return this.$te(key) ? this.$t(key) : filterKey
    },
  },
}
</script>

<style scoped>
/* `.filters-sidebar`/`.fsb-body` are kept on the elements below for other shared look-and-feel
   classes — `.fsb-header`/`.fsb-title`/`.fsb-footer`/etc. This panel sets explicit values for
   every property it needs, on dedicated classes, rather than relying on rules that other global
   stylesheets might define under the same names: the cascade applies per PROPERTY, and the
   bundler's CSS chunk ordering across routes is non-deterministic (see the "Conflicting order"
   mini-css-extract-plugin build warnings), so a specificity fight against a global selector isn't
   safe to rely on. */
.search-new-filters-panel {
  position: sticky;
  top: 58px;
  /* Shortened ~10px (viewport-relative calc is the right unit here since the panel tracks window
     height). Bump the subtracted offset to trim more. */
  height: calc(100vh - 210px);
  min-height: 320px;
  overflow: hidden;
}
.search-new-fsb-body {
  /* `min-height: 0` alongside `flex: 1`: a flex item's automatic minimum size is otherwise its
     content size, which alone can defeat `overflow-y: auto` and grow instead of scroll. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Only the FIELD-level classes (fsb-section-header, fsb-section-title, fsb-section-body,
   fsb-chevron, fsb-field, the fsb-select/fsb-segmented/fsb-sri/fsb-range families) are styled
   globally in main.scss. The SHELL classes below (white card, header, footer, buttons) are
   not global, so they are defined here, scoped. */
.filters-sidebar {
  width: 300px;
  flex-shrink: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.fsb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}
.fsb-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  font-family: 'Inter', sans-serif;
}

.fsb-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
  gap: 8px;
}
.fsb-reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  padding: 6px 10px;
  transition: all 0.13s;
  white-space: nowrap;
}
.fsb-reset-btn:hover:not(:disabled) {
  border-color: #d1d5db;
  color: #374151;
}
.fsb-reset-btn:disabled {
  color: #d1d5db;
  cursor: default;
}
.fsb-apply-btn {
  flex: 1;
  padding: 8px 12px;
  background: #e05537;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  color: white;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: background 0.13s;
  white-space: nowrap;
}
.fsb-apply-btn:hover {
  background: #c9472a;
}
</style>

